// src/components/Chat/groupPermissions.ts
import type { GroupRole } from '../../types';

/**
 * Действия над участником группы, которые можно показать в меню.
 *
 * Логика вынесена отдельно от диалога: правила прав проще проверять
 * модульными тестами, чем через клики по MUI-меню.
 */
export type MemberActionId = 'promote' | 'demote' | 'remove' | 'leave';

export interface MemberActionsContext {
    /** id текущего пользователя — по нему находим свою строку в составе. */
    currentUserId: string | null;
    /** Роль текущего пользователя в этой группе. */
    currentUserRole: GroupRole;
    /** Сколько админов в группе: последнего снимать и удалять нельзя. */
    adminCount: number;
}

/** Есть ли у роли права администрирования. */
export const isGroupAdmin = (role: GroupRole | null | undefined): boolean =>
    role === 'admin';

/**
 * Доступные действия над участником.
 *
 * - свою строку не удаляют: из группы выходят через «Выйти из группы»;
 * - управлять составом может только админ;
 * - последнего админа нельзя ни снять, ни удалить — иначе группа останется
 *   без управления.
 */
export const getMemberActions = (
    target: { userId: string; role: GroupRole },
    { currentUserId, currentUserRole, adminCount }: MemberActionsContext,
): MemberActionId[] => {
    if (currentUserId !== null && target.userId === currentUserId) return ['leave'];
    if (!isGroupAdmin(currentUserRole)) return [];

    if (target.role === 'admin') {
        // Снять/удалить можно, только если в группе останется другой админ.
        return adminCount > 1 ? ['demote', 'remove'] : [];
    }

    return ['promote', 'remove'];
};

/** Согласование слова «участник» с числом: 1 участник, 2 участника, 5 участников. */
export const formatMemberCount = (count: number): string => {
    const abs = Math.abs(count) % 100;
    const last = abs % 10;

    if (abs > 10 && abs < 20) return `${count} участников`;
    if (last === 1) return `${count} участник`;
    if (last >= 2 && last <= 4) return `${count} участника`;

    return `${count} участников`;
};

/**
 * Подзаголовок шапки чата: «ГРУППА • 3 участника».
 * Если бэкенд ещё не прислал количество — остаётся просто «ГРУППА».
 */
export const groupSubtitle = (memberCount: number | null | undefined): string => {
    if (typeof memberCount !== 'number' || !Number.isFinite(memberCount) || memberCount < 1) {
        return 'ГРУППА';
    }

    return `ГРУППА • ${formatMemberCount(memberCount)}`;
};
